import React from "react";
import { Link } from "react-router-dom";
import MaterialTable, { Column } from "@material-table/core";
import { Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { getTeamByOrgId_organization_team as TeamMember, UserRole } from "types/gql-types";
import PhoneNumber from "components/phone-number/phone-number";
import { CopyButton } from "components/haven-design/copy-button";
import { ArchiveUser } from "./archive";

// to be used in map on `user.roles` array
const formatRoles = (role: UserRole) => {
  switch (role) {
    case UserRole.PROPERTY_MANAGER:
      return "Property Manager";
    case UserRole.SUPERVISOR:
      return "Supervisor";
    case UserRole.CLIENT:
      return "Applicant";
    case UserRole.ADMIN:
      return "Administrator";
    default:
      return role;
  }
};


// ---- custom columns ----

type ActionProps = {
  rowData: TeamMember;
  value: string;
}

const ActionColumn = (props: ActionProps) => {
  const { rowData } = props;
  const userId: string = props.value;

  return (
    <div style={{ whiteSpace: "nowrap" }}>
      <Link to={`/dash/manager/settings/team/${userId}`}>Edit</Link>
      {" | "}
      <ArchiveUser user={rowData} />
    </div>
  );
};

type TypeProps = {
  value: UserRole[];
}
const TypeColumn = ({ value }: TypeProps) => {
  const roles = value.map(formatRoles).join(", ");
  return <div>{roles}</div>;
};

type StatusProps = {
  value: boolean;
}
const StatusColumn = ({ value }: StatusProps) => (
  <div style={{
    color: value ? "#666" : "#000",
    fontStyle: value ? "italic" : "normal",
  }}>
    {value ? "Inactive" : "Active"}
  </div>
);

// ---- user table ----
type TableProps = {
  rowData: TeamMember[];
}

const emailStyles = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "260px",
  display: "inline-block",
} as React.CSSProperties;

const columns: Array<Column<TeamMember>> = [
  { field: "fullName",
    title: "Name",
    render: ({ fullName, id }) => (
      <Link to={`/dash/manager/settings/team/${id}`}>
        {fullName}
        <EditIcon style={{ fontSize: "1rem", marginLeft: 4, verticalAlign: "middle" }} />
      </Link>
    ) },
  { field: "email",
    title: "Email",
    render: ({ email }) => {
      if (!email) return "";
      return <Tooltip title={email}>
        <span style={emailStyles}><CopyButton text={email} />{email}</span>
      </Tooltip>;
    } },
  { field: "directPhone", title: "Phone", render: ({ directPhone }) => <PhoneNumber number={directPhone ?? ""}/> },
  { field: "title", title: "Title" },
  { field: "roles", title: "Account Type", render: ({ roles }) => <TypeColumn value={roles} /> },
  { field: "isArchived", title: "Status", render: ({ isArchived }) => <StatusColumn value={isArchived} /> },
  { field: "id", title: "Action", render: (data) => <ActionColumn value={data.id} rowData={data} /> },
];

const TeamTable: React.FC<TableProps> = (props) => {
  const users = props.rowData;
  let pageSize = Math.min(users.length, 250);
  if (pageSize % 10 !== 0) {
    pageSize = pageSize - (pageSize % 10) + 10;
  }
  const division = Math.floor(pageSize / 5);
  return (
    <MaterialTable
      title="Team Settings"
      components={{
        Container: function Container (containerProps) {
          return <div {...containerProps}/>;
        },
      }}
      data={users}
      columns={columns}
      options={{
        showTitle: false,
        pageSize,
        pageSizeOptions: [...Array(pageSize + 1).keys()]
          .filter((num) => num !== 0 && num % division === 0),
        rowStyle: (rowData) => ({
          backgroundColor: rowData.isArchived ? "#f5f5f5" : "white",
          color: rowData.isArchived ? "#666" : "#000",
        }),
      }}
    />
  );
};

export default TeamTable;
